/**
 * InvoiceReplyCardConnected — wrapper que conecta hooks de invoice outreach
 * al ReplyCard shared. Billing Phase 2. Display-only: la reply nunca cambia
 * el status de la invoice — responder acá solo crea un draft para revisión.
 */
import { useTranslation } from 'react-i18next';
import { ReplyCard } from '../outreach/ReplyCard.js';
import type { NormalizedReply, ReplyCardLabels } from '../outreach/types.js';
import { useComposeInvoiceReply, type InvoiceOutreachReply } from '../../hooks/useInvoiceOutreach.js';

interface InvoiceReplyCardConnectedProps {
  reply: InvoiceOutreachReply;
  invoiceId: number;
}

export function InvoiceReplyCardConnected({ reply, invoiceId }: InvoiceReplyCardConnectedProps) {
  const { t } = useTranslation();
  const composeReply = useComposeInvoiceReply(invoiceId);

  const normalized: NormalizedReply = {
    id: reply.id,
    fromAddress: reply.fromAddress,
    subject: reply.subject,
    bodyText: reply.bodyText,
    receivedAt: reply.receivedAt,
    timestamp: reply.receivedAt,
    direction: 'inbound',
  };

  const labels: ReplyCardLabels = {
    inbound: t('billing.outreach.replies.inbound'),
    outbound: '',
    possible: '',
    verified: '',
    confirm: '',
    dismiss: '',
    from: t('billing.outreach.replies.from'),
    to: '',
    subject: t('billing.outreach.replies.subject'),
    received: t('billing.outreach.replies.received'),
    sent: '',
    showQuote: '',
    hideQuote: '',
    replyBtn: t('billing.outreach.reply'),
    sendReplyBtn: t('composer.send'),
    sending: t('billing.outreach.composing'),
    cancel: t('common.cancel'),
    attachBtn: '',
    sizeExceeded: '',
    replyTo: t('billing.outreach.replyTo', { to: '{to}' }),
    composerPlaceholder: t('billing.outreach.composing'),
    actionFailed: t('billing.outreach.actionFailed'),
  };

  return (
    <ReplyCard
      reply={normalized}
      labels={labels}
      onReply={({ bodyHtml, bodyText }) =>
        composeReply.mutate({ replyId: reply.id, bodyHtml, bodyText })
      }
      replyState={{ isPending: composeReply.isPending, isError: composeReply.isError, error: composeReply.error, reset: composeReply.reset }}
    />
  );
}
